import * as React from "react";
import {
  mergeStyleSets,
  Panel,
  TextField,
  PrimaryButton,
  DetailsList,
  DetailsListLayoutMode,
  SelectionMode,
  Spinner,
  ScrollablePane,
  ScrollbarVisibility,
  ConstrainMode,
  PanelType,
  Stack,
  IStackTokens,
  Selection,
  IObjectWithKey,
  CommandBar,
  StickyPositionType,
  Sticky,
  ICommandBarItemProps,
  ICommandBarStyles
} from "@fluentui/react";
import { INameValuePairIndex } from "../../interfaces/INameValuePairIndex";
import { DatasourceService } from "../../services/DatasourceService";
import { dataSourceItem } from "../../redux/models/reduxModels";
import { IDatasourceQueryDto } from "../../interfaces/IDatasourceQueryDto";
import { setDatasourceValues } from "../../redux/modules/datasource";
import { connect } from "react-redux";
import { DetailListBuilder } from "../helpers/detailListBuilder";
import { panelStyles, detailListStyles } from "../../config/controlStyles";
import { LanguageContext } from "../../../../utilities/LocalizationModule";
import { RenderHelper } from "../helpers/renderHelper";
import CollapsibleSection from "../directoryComponents/advancedSearch/components/CollapsibleSection"
import { isMobile } from "../../styles/mobileStyles";
import { CMAddDatasourceItemPanel } from "../../../../contentManager/Panels/CMAddDatasourceItemPanel";
import { DatasourceHelper } from "../helpers/dataSourceHelper";

// Styles
const tableHeadingHeight = 60;
const panelHeadingHeight = 32;
const sectionTitleHeight = 34;
const searchFieldsItemHeight = 80;
const commandBarStyle: Partial<ICommandBarStyles> = {
  root: { padding: "0 0 0 15px", height: "25px" },
};

let alwaysSpaceTaken = panelHeadingHeight + sectionTitleHeight + tableHeadingHeight;
//Props + Redux


const mapDispatchToProps = {
  setDatasourceValues
};

type ReduxProps = typeof mapDispatchToProps;

interface DatasourcePanelReduxProps extends ReduxProps {
  updateInputValue: (newValue: any) => void;
  closePanel: () => void;
  isOpen: boolean;
  datasourceService: DatasourceService;
  datasourceItem: dataSourceItem;
  searchInputs: INameValuePairIndex[];
  updateSearchInputs: (index: number, value: string) => void;
  boundedField: string;
  displayName: string;
  type?: PanelType;
  dataSourceItem?: dataSourceItem;
  dataSourceHelper?: DatasourceHelper;
}

export const UnconnectedDataSourcePanel: React.FunctionComponent<DatasourcePanelReduxProps> = props => {
  const languageStrings = React.useContext(LanguageContext);
  const dismissPanel = React.useCallback(() => props.closePanel(), []);
  const [items, setItems] = React.useState([]);
  const [columns, setColumns] = React.useState([]);
  const [showTable, setShowTable] = React.useState(false);
  const [isSearching, setIsSearching] = React.useState<boolean>(false);
  const [showFields, setShowFields] = React.useState(true);
  const [selectedItem, setSelectedItem] = React.useState<IObjectWithKey>();
  const [topCommandItems, setTopCommandItems] = React.useState<ICommandBarItemProps[]>([]);
  const [isAddDatasourceItemPanelOpen, setIsAddDatasourceItemPanelOpen] = React.useState(false);

  const toggleShowFields = () => {
    setShowFields(currentVal => !currentVal);
  }

  const selection = React.useMemo(
    () =>
      new Selection({
        onSelectionChanged: () => {
          setSelectedItem(selection.getSelection()[0]);
        },
        selectionMode: SelectionMode.single,
      }),
    []);

  let detailListBuilder: DetailListBuilder;

  const onChangeTextHandle = (event: any, newValue?: string) => {
    props.updateSearchInputs(parseInt(event.target.name), newValue);
  };

  React.useEffect(() => {
    if (props.isOpen) {
      const setupAsyncWrapper = async () => {
        await search();
      };
      setupAsyncWrapper();
    }
  }, [props.isOpen]);

  const search = async () => {
    resetTable();
    
    const searchParams = props.searchInputs
      .map(item => ({ name: item.name, value: item.value?.trim() }))
      .filter(item => item.value);

    if (!searchParams.length) return;

    setIsSearching(true);
    // resetTable();

    const request: IDatasourceQueryDto = {
      id: props.datasourceItem.id,
      tenantId: props.datasourceItem.tenantId,
      typeId: props.datasourceItem.type.id,
      typeName: props.datasourceItem.type.name,
      name: props.datasourceItem.name,
      queryParams: searchParams,
      returnOnlyQueriedProperties: false,
      allowCache: props.datasourceItem.allowCache
    };

    const result = await props.datasourceService.Search(request);
    if (result.length > 0) {
      detailListBuilder = new DetailListBuilder();
      let tableData = detailListBuilder.getTableData(result, false);
      setColumns(tableData.columns);
      setItems(tableData.rows);
      setShowFields(false);
    }
    else {
      setShowFields(true);
    }

    setShowTable(true);
    setIsSearching(false);
  };

  const resetTable = () => {
    setColumns([]);
    setItems([]);
    setShowFields(true);
    setShowTable(false);
  };

  const searchItems = props.searchInputs.map((item, index) => {
    return (
      <Stack.Item key={index}>
        <TextField
          label={item.name}
          id={item.name}
          value={item.value ?? ""}
          onChange={onChangeTextHandle}
          name={item.index.toString()}
          className="xb-sp-input"
        />
      </Stack.Item>
    );
  });

  const handleItemSubmitted = (item: any): void => {
    const propertyKeys = Object.keys(item); //keys of the data source, not the sharepoint columns
    if (propertyKeys) {
      let boundObject = {};
      propertyKeys.forEach((key) => {
        if (item[key]) {
          const boundField = props.datasourceItem?.boundFields?.find(field => {
            return field?.datasourceField.toLowerCase().trim() == key.toLowerCase().trim()
          }); //check if any of the data source fields have a mapping.
          if (boundField) { //if a mapping exists
            const names = boundField.name.split(",").map(name => name.toLowerCase().trim());
            names.forEach(name => {
              boundObject[name] = item[key];
            })
          } else {
            boundObject[key] = item[key];
          }
        }
      });
      props.setDatasourceValues(boundObject); //redux. 
      props.updateInputValue(item[props.boundedField.toLowerCase()]);
    }
    props.closePanel();
  };
  const small = isMobile() ? 120 : 84;
  const large = isMobile() ? 450 : 415;
  const searchFieldsHeight = searchFieldsItemHeight * (searchItems.length + 1);
  alwaysSpaceTaken = showFields ? Math.min(alwaysSpaceTaken + searchFieldsHeight, large) : Math.min(alwaysSpaceTaken, small);

  const classNames = mergeStyleSets({
    wrapper: {
      marginLeft: -24,
      marginRight: -24,
      marginBottom: -20,
      height: `calc(100vh - ${alwaysSpaceTaken}px)`,
      position: "relative"
    }
  });

  React.useEffect(() => {
    const items: ICommandBarItemProps[] = [
      {
        key: "select",
        disabled: !selectedItem,
        text: `${"Select"}`,
        iconProps: { iconName: "CheckMark" },
        onClick: () => handleItemSubmitted(selectedItem),
      }
    ]

    if (props.dataSourceItem?.allowAdd) {
      items.push(
        {
          key: "add",
          text: `${languageStrings.Add}`,
          iconProps: { iconName: "Add" },
          onClick: () => setIsAddDatasourceItemPanelOpen(true),
        }
      )
    }

    setTopCommandItems(items)
  }, [selectedItem, props.dataSourceItem])

  const detailList =
    showTable ? (<>

      <Sticky stickyPosition={StickyPositionType.Header}>
        <CommandBar
          styles={commandBarStyle}
          items={topCommandItems}
          ariaLabel="Select an item"
        />
      </Sticky>
      {items.length > 0 ? <DetailsList
        items={items}
        columns={columns}
        compact={false}
        setKey="set"
        styles={detailListStyles}
        layoutMode={DetailsListLayoutMode.justified}
        constrainMode={ConstrainMode.unconstrained}
        onRenderDetailsHeader={RenderHelper.onRenderDetailsHeader}
        selectionPreservedOnEmptyClick={true}
        ariaLabelForSelectionColumn="Toggle selection"
        ariaLabelForSelectAllCheckbox="Toggle selection for all items"
        checkButtonAriaLabel="Row checkbox"
        onItemInvoked={handleItemSubmitted}
        selection={selection}
        selectionMode={SelectionMode.single}
      /> : <p style={{ textAlign: "center", marginTop: "40px" }}>{languageStrings.NoItemsFound}</p>}
    </>
    ) : null;

  const stackTokens: IStackTokens = {
    childrenGap: 15
  };

  const SearchItems = (
    <>
      <Stack tokens={stackTokens}>
        {searchItems}
      </Stack>
      <div className="xb-panel-button-container">
        <PrimaryButton text={languageStrings.Search} disabled={isSearching} allowDisabledFocus onClick={() => search()}>
          {isSearching ? <Spinner /> : null}
        </PrimaryButton>
      </div>
    </>
  )

  const IsOfficeJSAvailable: boolean = React.useMemo(() => typeof Office !== "undefined" && typeof Office.context?.diagnostics?.host !== "undefined", []);

  const panelWidth: string = IsOfficeJSAvailable ?
    props?.type === PanelType.customNear ? '360px' : undefined
    : "700px";

  return (
    <Panel
      headerText={props.displayName} //`${languageStrings.Search} ` + 
      isOpen={props.isOpen}
      styles={panelStyles}
      onDismiss={dismissPanel}
      closeButtonAriaLabel="Close"
      type={props?.type ?? PanelType.medium}
      customWidth={panelWidth}
    >
      <CollapsibleSection
        title={languageStrings.SearchFields}
        isExpanded={showFields}
        toggleIsExpanded={toggleShowFields}
        item={SearchItems}
        itemCount={searchItems.length + 1}
        itemHeight={searchFieldsItemHeight}
      />
      <div className="clear"></div>
      <div className={classNames.wrapper}>
        <ScrollablePane scrollbarVisibility={ScrollbarVisibility.auto}>
          <div>{detailList}</div>
        </ScrollablePane>
      </div>
      {isAddDatasourceItemPanelOpen && 
                <CMAddDatasourceItemPanel 
                    panelIsOpen={isAddDatasourceItemPanelOpen} 
                    closePanel={() => setIsAddDatasourceItemPanelOpen(false)}
                    displayName={props.displayName}
                />}
    </Panel>
  );
};



export const DataSourcePanel = connect(null, mapDispatchToProps)(UnconnectedDataSourcePanel);
